import { PlusIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { LayoutVariableDefinitionTypeToLabel } from 'dashboard/models/layout-variable-definitions';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  useArchiveCaseVariableDefinitionMutation,
  useCreateCaseVariableDefinitionMutation,
} from 'dashboard/redux/api/case-variable-definition';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { Permission } from 'lib/permissions';

const StickyTableHead = styled(TableHead).attrs(() => ({ sticky: true }))`
  z-index: ${Z_INDICES.sticky};
`;

interface FormCaseTemplateSettingsQueriesProps {
  caseVariableDefinitions: CaseVariableDefinition[];
  caseTemplateId: string;
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
}

const FormCaseTemplateSettingsQueries = ({
  caseTemplateId,
  caseVariableDefinitions,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
}: FormCaseTemplateSettingsQueriesProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasProductFeatureEnabled = hasCasesBasic || hasCasesEnhanced || hasCasesEnterprise;
  const hasWritePermission = usePermission(Permission.CaseTemplateWrite);
  const editable = hasProductFeatureEnabled && hasWritePermission;

  const [createVariableDefinition, { error: createVariableDefinitionError }] =
    useCreateCaseVariableDefinitionMutation();
  const [archiveVariableDefinition] = useArchiveCaseVariableDefinitionMutation();
  const handleCreateCaseVariableDefinition = useCallback(async () => {
    createVariableDefinition(caseTemplateId).then((res) => {
      if ('data' in res) {
        const {
          data: { data: newVariableDefinition },
        } = res;

        navigate(
          `/dashboard/case-templates/${caseTemplateId}/settings/queries/${newVariableDefinition.id}`,
        );
      }
    });
  }, [caseTemplateId, createVariableDefinition, navigate]);

  useRTKErrorHandler({
    error: createVariableDefinitionError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            <Permissioned permission={Permission.CaseTemplateWrite}>
              <Button
                aria-label='Add new query'
                id='case-template-show__create-query-button'
                variant='basic'
                size='md'
                disabled={!editable}
                prefixIcon={<PlusIcon />}
                onClick={handleCreateCaseVariableDefinition}
              >
                Add
              </Button>
            </Permissioned>
          }
        >
          Queries
        </Card.Title>

        <Table style={{ tableLayout: 'fixed' }}>
          <StickyTableHead>
            <TableRow>
              <TableHeaderCell width='25%'>Name</TableHeaderCell>
              <TableHeaderCell width='20%'>Data source</TableHeaderCell>
              <TableHeaderCell width='25%'>Created at</TableHeaderCell>
              <TableHeaderCell width='10%' />
            </TableRow>
          </StickyTableHead>
          <TableBody>
            {caseVariableDefinitions.map((caseVariableDefinition) => {
              return (
                <TableRow key={caseVariableDefinition.id}>
                  <TableCell>{caseVariableDefinition.attributes.key}</TableCell>
                  <TableCell>
                    {
                      LayoutVariableDefinitionTypeToLabel[
                        caseVariableDefinition.attributes['variable-definition'].type
                      ]
                    }
                  </TableCell>
                  <TableCell>
                    {DateTime.fromISO(caseVariableDefinition.attributes['created-at']).toFormat(
                      DATE_AND_TIME_FORMAT,
                    )}
                  </TableCell>
                  <TableCell>
                    <ContextMenu
                      variant='basic'
                      size='sm'
                      disabled={!editable}
                      data-test={`query-table__row--${caseVariableDefinition.id}__actions`}
                      items={[
                        {
                          id: 'edit',
                          onClick: () =>
                            navigate(
                              `/dashboard/case-templates/${caseTemplateId}/settings/queries/${caseVariableDefinition.id}`,
                            ),
                          variant: 'basic',
                          label: 'Edit',
                        },
                        {
                          id: 'archive',
                          onClick: () => {
                            archiveVariableDefinition(caseVariableDefinition).then(() => {
                              dispatch(
                                showMessage('Query archived successfully', MessageType.Success),
                              );
                            });
                          },
                          variant: 'danger',
                          label: 'Archive',
                        },
                      ]}
                      renderItem={(item) => (
                        <ContextMenu.Item
                          id={`query-table__row--${caseVariableDefinition.id}__${item.id}`}
                          key={`query-table__row--${caseVariableDefinition.id}__${item.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            item.onClick?.();
                          }}
                          variant={item.variant}
                        >
                          {item.label}
                        </ContextMenu.Item>
                      )}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default FormCaseTemplateSettingsQueries;
